import React, { useEffect, useState, useRef } from 'react';
import { User, LogOut, MessageCircle, ChevronDown, Building2, ArrowLeft, ShieldAlert } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { useChat } from '../contexts/ChatContext';
export function Header() {
  const {
    user,
    logout,
    isImpersonating,
    stopImpersonating
  } = useAuth();
  const {
    unreadTotal
  } = useChat();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  useEffect(() => {
    if (!showUserMenu) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setShowUserMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showUserMenu]);
  const handleLogout = () => {
    logout();
    navigate('/login');
  };
  const handleChatClick = () => {
    navigate('/employee/chat');
  };
  const handleStopImpersonating = () => {
    stopImpersonating();
    navigate('/admin/employees');
  };
  // Generate initials from name
  const getInitials = (name?: string) => {
    if (!name) return '?';
    return name.split(' ').map(n => n[0]).slice(0, 2).join('').toUpperCase();
  };
  // Generate a consistent color from name
  const getAvatarColor = (name?: string) => {
    const colors = ['from-violet-500 to-purple-600', 'from-blue-500 to-indigo-600', 'from-emerald-500 to-teal-600', 'from-orange-500 to-amber-600', 'from-rose-500 to-pink-600', 'from-cyan-500 to-sky-600'];
    if (!name) return colors[0];
    const idx = name.charCodeAt(0) % colors.length;
    return colors[idx];
  };
  return <header className="shrink-0 z-40 bg-white border-b border-gray-100 shadow-sm" data-id="element-579">
      {/* Impersonation Banner */}
      {isImpersonating && <div className="bg-amber-500 text-white px-4 py-2 flex items-center justify-between gap-3" data-id="element-580">
          <div className="flex items-center gap-2 min-w-0" data-id="element-581">
            <ShieldAlert size={16} className="shrink-0" data-id="element-582" />
            <span className="text-sm font-semibold truncate" data-id="element-583">
              Viendo como: <span className="font-bold" data-id="element-584">{user?.name}</span>
              {user?.company && <span className="opacity-80" data-id="element-585"> — {user.company}</span>}
            </span>
          </div>
          <button onClick={handleStopImpersonating} className="flex items-center gap-1.5 px-3 py-1 bg-white/20 hover:bg-white/30 rounded-lg text-sm font-bold transition-colors shrink-0" data-id="element-586">
            <ArrowLeft size={14} data-id="element-587" />
            Volver al Admin
          </button>
        </div>}
      <div className="px-4 lg:px-8 h-16 flex items-center justify-between" data-id="element-588">
        {/* Left — Logo */}
        <div className="flex items-center gap-3" data-id="element-589">
          <img src="https://i.imgur.com/bxPZvDb.png" alt="Cricket Logo" className="h-9 w-auto object-contain" data-id="element-590" />
        </div>

        {/* Right — Actions */}
        <div className="flex items-center gap-1.5" data-id="element-591">
          {/* Company Badge (Desktop) - Only for employees with company info */}
          {user?.role === 'employee' && user.company && <div className="hidden sm:flex items-center gap-2 px-3 py-1.5 bg-gray-50 rounded-lg border border-gray-100 mr-2" data-id="element-592">
              <Building2 size={14} className="text-gray-400" data-id="element-593" />
              <div className="flex flex-col leading-none" data-id="element-594">
                <span className="text-xs font-bold text-gray-700 truncate max-w-[120px]" data-id="element-595">
                  {user.company}
                </span>
                {user.branch && <span className="text-[10px] text-gray-400 truncate max-w-[120px]" data-id="element-596">
                    {user.branch}
                  </span>}
              </div>
            </div>}

          {/* Chat / Support */}
          <button onClick={handleChatClick} className="relative flex items-center gap-1.5 px-3 py-2 rounded-xl text-gray-500 hover:text-gray-900 hover:bg-gray-50 transition-all text-sm font-medium" aria-label="Soporte" data-id="element-597">
            <MessageCircle size={17} data-id="element-598" />
            <span className="hidden sm:inline" data-id="element-599">Soporte</span>
            {unreadTotal > 0 && <span className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-emerald-500 text-white text-[9px] font-bold rounded-full flex items-center justify-center" data-id="element-600">
                {unreadTotal}
              </span>}
          </button>

          {/* Divider */}
          <div className="w-px h-6 bg-gray-200 mx-1" data-id="element-601" />

          {/* User Menu */}
          <div className="relative" ref={menuRef} data-id="element-602">
            <button onClick={() => setShowUserMenu(!showUserMenu)} className="flex items-center gap-2.5 pl-1 pr-2.5 py-1.5 rounded-xl hover:bg-gray-50 transition-all" data-id="element-603">
              {/* Avatar with gradient + initials */}
              <div className={`w-8 h-8 rounded-lg bg-gradient-to-br ${getAvatarColor(user?.name)} flex items-center justify-center shadow-sm shrink-0`} data-id="element-604">
                <span className="text-white text-xs font-bold tracking-wide" data-id="element-605">
                  {getInitials(user?.name)}
                </span>
              </div>
              <div className="hidden sm:block text-left" data-id="element-606">
                <p className="text-sm font-semibold text-gray-900 leading-tight" data-id="element-607">
                  {user?.name}
                </p>
                <p className="text-[11px] text-gray-400 leading-tight capitalize" data-id="element-608">
                  {user?.role === 'admin' ? 'Administrador' : 'Empleado'}
                </p>
              </div>
              <ChevronDown size={14} className={`text-gray-400 transition-transform hidden sm:block ${showUserMenu ? 'rotate-180' : ''}`} data-id="element-609" />
            </button>

            {showUserMenu && <div className="absolute right-0 mt-2 w-60 bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden z-50" data-id="element-610">
                {/* User info */}
                <div className="p-4 border-b border-gray-100" data-id="element-611">
                  <div className="flex items-center gap-3 mb-3" data-id="element-612">
                    <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${getAvatarColor(user?.name)} flex items-center justify-center shadow-sm shrink-0`} data-id="element-613">
                      <span className="text-white text-sm font-bold" data-id="element-614">
                        {getInitials(user?.name)}
                      </span>
                    </div>
                    <div className="min-w-0" data-id="element-615">
                      <p className="font-semibold text-gray-900 text-sm truncate" data-id="element-616">
                        {user?.name}
                      </p>
                      <p className="text-xs text-gray-400 truncate" data-id="element-617">
                        {user?.email}
                      </p>
                    </div>
                  </div>

                  {/* Company info in dropdown */}
                  {user?.role === 'employee' && user.company && <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 rounded-lg text-xs text-gray-600" data-id="element-618">
                      <Building2 size={14} className="text-gray-400 shrink-0" data-id="element-619" />
                      <div className="min-w-0" data-id="element-620">
                        <p className="font-medium truncate" data-id="element-621">{user.company}</p>
                        {user.branch && <p className="text-gray-400 truncate" data-id="element-622">
                            {user.branch}
                          </p>}
                      </div>
                    </div>}
                </div>

                <button onClick={handleLogout} className="w-full px-4 py-3 text-left text-sm text-red-600 hover:bg-red-50 transition-colors flex items-center gap-2.5 font-medium" data-id="element-623">
                  <LogOut size={15} data-id="element-624" />
                  Cerrar Sesión
                </button>
              </div>}
          </div>
        </div>
      </div>
    </header>;
}